"use client";

import { Button, Input, Label, toast } from "@devhop/ui";
import { useEffect, useMemo, useState } from "react";
import {
  useAddWorkshopJobLaborMutation,
  useWorkshopLaborPresetsQuery,
} from "../api/queries";
import { formatWorkshopMoney } from "../lib/workshop-labels";
import { workshopToastError } from "../lib/workshop-user-messages";

export function WorkshopAddLaborForm({
  shopId,
  jobId,
  open,
  onAdded,
  formId = "workshop-add-labor-form",
}: {
  shopId: string;
  jobId: string;
  open: boolean;
  onAdded?: () => void;
  formId?: string;
}) {
  const [q, setQ] = useState("");
  const [customName, setCustomName] = useState("");
  const [customPrice, setCustomPrice] = useState("");

  const presetsQ = useWorkshopLaborPresetsQuery(
    shopId,
    useMemo(() => ({ q: q.trim() || undefined, limit: 30 }), [q]),
    open,
  );
  const addMut = useAddWorkshopJobLaborMutation(shopId);

  useEffect(() => {
    if (open) {
      setQ("");
      setCustomName("");
      setCustomPrice("");
    }
  }, [open]);

  async function addPreset(skuId: string) {
    try {
      await addMut.mutateAsync({
        jobId,
        body: { shopProductSkuId: skuId, qty: 1 },
      });
      toast.success("ເພີ່ມຄ່າແຮງແລ້ວ");
      onAdded?.();
    } catch (err) {
      toast.error(workshopToastError(err));
    }
  }

  async function submitCustom(e?: React.FormEvent) {
    e?.preventDefault();
    const name = customName.trim();
    const price = Number(customPrice);
    if (!name || !Number.isFinite(price) || price < 0) {
      toast.error("ໃສ່ຊື່ງານ ແລະ ລາຄາ");
      return;
    }
    try {
      await addMut.mutateAsync({
        jobId,
        body: { name, unitPrice: price, qty: 1 },
      });
      toast.success("ເພີ່ມຄ່າແຮງແລ້ວ");
      onAdded?.();
    } catch (err) {
      toast.error(workshopToastError(err));
    }
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor={`${formId}-preset-q`}>ເລືອກຈາກລາຍການສຳເລັດຮູບ</Label>
        <Input
          id={`${formId}-preset-q`}
          placeholder="ຄົ້ນຊື່ຄ່າແຮງ…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        {presetsQ.isLoading ? (
          <p className="text-muted-foreground text-xs">ກຳລັງໂຫຼດ…</p>
        ) : (
          <ul className="max-h-44 space-y-1 overflow-y-auto">
            {(presetsQ.data?.data ?? []).map((p) => (
              <li key={p.shopProductSkuId}>
                <button
                  type="button"
                  className="flex h-11 w-full items-center justify-between rounded-lg border border-border bg-background px-3 text-left text-sm hover:border-primary/40"
                  disabled={addMut.isPending}
                  onClick={() => void addPreset(p.shopProductSkuId)}
                >
                  <span className="truncate">{p.name}</span>
                  <span className="shrink-0 text-xs tabular-nums">
                    {formatWorkshopMoney(p.price, p.currency)}
                  </span>
                </button>
              </li>
            ))}
            {(presetsQ.data?.data ?? []).length === 0 ? (
              <p className="text-muted-foreground text-xs">
                ບໍ່ມີ service SKU — ພິມເອງດ້ານລຸ່ມ
              </p>
            ) : null}
          </ul>
        )}
      </div>

      <form
        id={formId}
        className="space-y-3 rounded-xl border border-border bg-muted/15 p-3"
        onSubmit={(e) => void submitCustom(e)}
      >
        <p className="font-medium text-sm">ພິມເອງ</p>
        <div className="space-y-1">
          <Label htmlFor={`${formId}-name`}>ຊື່ງານ</Label>
          <Input
            id={`${formId}-name`}
            value={customName}
            onChange={(e) => setCustomName(e.target.value)}
            placeholder="ເຊັ່ນ ປ່ຽນນ້ຳມັນເຄື່ອງ"
            autoFocus
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor={`${formId}-price`}>ລາຄາ</Label>
          <Input
            id={`${formId}-price`}
            inputMode="numeric"
            // ຮັບສະເພາະຕົວເລກ (ພິມພ໌ໂຕໜັງສືບໍ່ໄດ້) + ສະແດງຕົວຄັ່ນຫຼັກພັນ 20,000
            value={
              customPrice ? Number(customPrice).toLocaleString("en-US") : ""
            }
            onChange={(e) =>
              setCustomPrice(e.target.value.replace(/[^0-9]/g, ""))
            }
            placeholder="0"
          />
        </div>
      </form>
    </div>
  );
}
